<script setup lang="ts">
// Vendor
import type { Entry } from "contentful";

// Types
import type { IBFFCity } from "@citizenm/foundation/shared/types/city";

const { locale, t } = useI18n();
const { params } = useRoute();

const localePath = useLocalePath();

definePageMeta({
  middleware: "cached-page",
});

const continent = computed(() =>
  "continent" in params ? params.continent : undefined,
);

if (!continent.value) {
  throw new Error("Continent not found");
}

const travelDataStore = useTravelDataStore();
const currencyStore = useCurrencyStore();
const snackBarStore = useSnackbarStore();

/**
 * Marketing related data
 */
const { data: continentMarketingData } =
  await useFetch<ContentfulOverviewPageResponse>(
    `/api/v2/marketing/overview-page/${continent.value}`,
    {
      query: {
        locale,
      },
      key: `continent-${continent.value}-${locale.value}`,
    },
  );
const content = computed(() => continentMarketingData.value?.content);

/**
 * Global location related data
 */
const { hotelsByContinent } = useHotels();
const location = computed(
  () =>
    continentMarketingData.value?.location as unknown as CfContinentSkeleton &
      Entry,
);
const propertyCodes = computed(() =>
  // @ts-expect-error fields is casted to unknown by Contentful
  (hotelsByContinent.value[location.value?.fields.code] as PropertyHotel[]).map(
    (hotel) => hotel.propertyCode,
  ),
);
const hotelCount = computed(() => propertyCodes.value?.length || 0);

if (!location.value) {
  throw new Error("Continent location data not found");
}

const { data: citiesInCurrentContinent } = useFetch<IBFFCity[]>(
  `/api/v2/cities/by-continent/${location.value.fields.code}`,
  {
    query: {
      locale,
    },
    key: `cities-in-continent-${location.value.fields.code}-${locale.value}`,
  },
);

/**
 * Availability related data
 */
travelDataStore.changeToLocationId(location.value.sys.id);

/* TODO
  The store should have a totalGuests AND guestsPerRoom.
  The totalGuests is the sum of all guests in all rooms.
  This is the safest way to ensure that the API call is correct and
  not to risk harming other areas where totalGuests is used.
*/
const { totalGuests } = useRate();
const apiQueryParams = {
  ...travelDataStore.getDataForQuery,
  totalGuests: totalGuests.value,
  hotelCodes: propertyCodes.value.join(","),
};

if (apiQueryParams.totalGuests! < apiQueryParams.numberOfRooms!) {
  apiQueryParams.totalGuests = apiQueryParams.numberOfRooms;
}

const { data: availabilityResponseData, status: availabilityStatus } =
  await useGoodBitsFetch<HotelRoomsResponse>("/offers/v1/hotel-rooms", {
    query: apiQueryParams,
    lazy: true,
    server: false,
    onResponseError: () => {
      snackBarStore.warning({
        text: t("website.error.loadingHotelAvailability"),
        timeout: false,
        scope: "global",
      });
    },
    key: `hotel-rooms-${location.value.fields.code}-${locale.value}`,
  });

/**
 * Merged list with availability data
 */
const cities = computed(() =>
  citiesInCurrentContinent.value?.map((city) => {
    const cityUrl = localePath({
      name: "hotels-continent-city",
      params: {
        continent: String(location.value.fields.slug),
        city: city.slug,
      },
    });

    const hotelRoomOffersForCity =
      availabilityResponseData.value?.hotelRoomOffers?.filter((offer) =>
        city.properties.some((property) => property.code === offer.hotelCode),
      );

    const propertyCountWithOffers = new Set(
      hotelRoomOffersForCity?.map((offer) => offer.hotelCode),
    ).size;

    const bestBookablePricesForCity =
      availabilityResponseData.value?.bestPrices?.filter((price) =>
        city.properties.some((property) => property.code === price.hotelCode),
      );

    /**
     * Get the lowest total gross amount and currency from the list of best bookable prices,
     * so we can display the best price for the city.
     */
    const {
      currency,
      totalGrossAmount = 0,
      totalGrossAmountExcl = 0,
    } = bestBookablePricesForCity?.reduce<TotalAmount>(
      (min, curr) =>
        min.totalGrossAmount === 0 ||
        curr.totalAmount.totalGrossAmount < (min?.totalGrossAmount || 0)
          ? curr.totalAmount
          : min,
      {
        currency: "local",
        totalGrossAmount: 0,
        totalGrossAmountExcl: 0,
        totalTaxAmount: 0,
        totalNetAmount: 0,
      },
    ) || {};

    return {
      // Metadata
      name: city.title,
      photos: [city.heroImage],
      roomsCount: city.properties.length,
      text: city.shortDescription,
      // Urls
      bookUrl: cityUrl,
      url: cityUrl,
      // Price
      pricePerNightIncTax: totalGrossAmount / travelDataStore.getNightsOfStay,
      pricePerNightExcTax:
        totalGrossAmountExcl / travelDataStore.getNightsOfStay,
      pricePerStayIncTax: totalGrossAmount,
      pricePerStayExcTax: totalGrossAmountExcl,
      currency,
      roomsAvailable: propertyCountWithOffers,
      loaded: ["success", "error"].includes(availabilityStatus.value),
    };
  }),
);

/**
 * Client side actions
 */
// TODO: align this process with other places in website that require continents and share state
await travelDataStore.checkTraveldata();

/**
 * Marketing / tracking related logic
 */
useContentfulPage(continentMarketingData, {
  setSeoMeta: true,
  pageTypeToTrack: PageTypeTrackingId.webpageContinent,
  ecommerceStage: "city-list",
});

// i18n settings
const setI18nParams = useSetI18nParams();
const localizedRouteParams = await useLocalizedRouteParams();
if (localizedRouteParams.value) {
  setI18nParams(localizedRouteParams.value);
}
</script>

<template>
  <div class="main-app continent-view" role="main" aria-labelledby="main-title">
    <div class="wrap">
      <section class="mt16">
        <HotelSearchBar bar has-shadow :submit-url="localePath('/book')" />
      </section>

      <section class="mt16">
        <h1 class="h5">
          {{
            t("website.general.location.xHotelsInLocation", {
              count: hotelCount,
              location: location?.fields.title,
            })
          }}
        </h1>

        <div class="actions-row">
          <CurrencyChangeSelect
            :label="$t('website.general.currency')"
            :labelLocalCurrency="$t('website.general.localCurrency')"
            :has-border="false"
          />
          <PriceTypeChange
            :label="$t('website.general.price')"
            :selected="travelDataStore.selectedPriceType"
            @change="travelDataStore.selectPriceType"
          />
        </div>
        <div class="mt16">
          <HotelList
            :selected-price-type="travelDataStore.selectedPriceType"
            :hotels="cities"
            :selected-currency="currencyStore.selectedCurrency"
          />
        </div>
      </section>
    </div>
    <div class="wrap explore mt32" v-if="content">
      <ContentfulSection v-for="c in content" :content="c" :key="c.sys.id" />
    </div>
  </div>
</template>
